import clsx from "clsx";
import {
  ElementType,
  FC,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import toast, { Renderable } from "react-hot-toast";
import { Tooltip } from "react-tooltip";
import {
  InsufficientAllowanceError,
  Msg,
  MsgErc20IncreaseAllowance,
  networkTxLink,
  Simulation,
  translateError,
  TxError,
  TxResult,
} from "rujira.js";
import lock from "../../assets/images/unlock.gif";
import { i18n } from "../../i18n";
import { AccountProvider } from "../../wallets";
import { Warning } from "../notices/Warning";
import { Decimal } from "../numbers/Decimal";
import { Button, ButtonProps } from "./Button";
import txSuccess from "../../assets/images/txsuccess.gif";
import txFail from "../../assets/images/txfail.gif";
import txSign from "../../assets/images/txsign.gif";
import { Loader } from "../loader/Loader";

// Helper to identify spacing/layout classes
const SPACING_CLASS_REGEX = /^(m|p)(l|r|t|b|x|y)?-\d+$/;
const isSpacingClass = (className: string) =>
  SPACING_CLASS_REGEX.test(className);

// Separate spacing classes from styling classes
const separateClasses = (className?: string) => {
  const classes = className?.split(" ") || [];
  return {
    spacing: classes.filter(isSpacingClass),
    styling: classes.filter((c) => !isSpacingClass(c)),
  };
};

export type TxButtonProps<T extends ElementType = "button"> = ButtonProps<T> & {
  accountProvider: AccountProvider;
  msg: Msg | null;
  SimulationComponent?: ElementType<{
    simulation?: Simulation;
    error?: Error;
  }>;
  onError?: (err: any) => void;
  onSuccess?: (res: TxResult) => void;
  hideSimulation?: boolean;
  toastOpts?: {
    loading?: Renderable;
    success?: Renderable | ((res: TxResult) => Renderable);
    error?: Renderable | ((err: any) => Renderable);
  };
};

export const TxButton: FC<TxButtonProps> = (props) => {
  const {
    accountProvider,
    msg,
    onSuccess,
    onError,
    SimulationComponent = DefaultSimulationComponent,
    children,
    toastOpts = {},
    className,
    hideSimulation,
    onClick,
    ...rest
  } = props;

  const [simulation, setSimulation] = useState<Simulation>();
  const [simulationError, setSimulationError] = useState<Error>();
  const { loading, success, error } = toastOpts;
  const [isSigning, setIsSigning] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isRejected, setIsRejected] = useState(false);

  const doSimulate = (msg: Msg | null) => {
    setSimulationError(undefined);
    setSimulation(undefined);
    if (msg) {
      const signer = accountProvider.signer(msg.account.address);
      signer
        .simulate(msg)
        .then(setSimulation)
        .catch((err) => {
          console.error(err);
          setSimulationError(err);
          onError && onError(new TxError(msg, err, "simulation"));
        });
    }
  };

  useEffect(() => {
    doSimulate(msg);
  }, [msg]);

  const handleSign = (e: any) => {
    if (isSigning || isSuccess || isRejected) return;
    if (!simulation) throw new Error(`Simulation required`);
    if (!msg) throw new Error(`Msg required`);

    setIsSigning(true);
    const signer = accountProvider.signer(msg.account.address);

    if (onClick) onClick(e);

    const p = signer
      .signAndBroadcast(simulation, msg)
      .then((res) => {
        onSuccess && onSuccess(res);
        setIsSigning(false);
        setIsSuccess(true);
        setTimeout(() => {
          setIsSuccess(false);
        }, 3000);
        return res;
      })
      .catch((err) => {
        console.error(err);
        onError && onError(new TxError(msg, err, "signAndBroadcast"));
        setIsSigning(false);

        setIsRejected(true);
        setTimeout(() => {
          setIsRejected(false);
        }, 3000);

        throw err;
      });

    toast.promise(p, {
      loading: loading || i18n.t("Submitting Transaction"),
      success: success || successHandler,
      error:
        error ||
        ((err: any) => {
          console.error(err);
          const defaultError = "Error Submitting Transaction";
          switch (typeof err) {
            case "string":
              return i18n.t(translateError(err) || defaultError);
            case "object":
              return i18n.t(
                "message" in err
                  ? translateError(err.message) || defaultError
                  : defaultError
              );

            default:
              return i18n.t(defaultError);
          }
        }),
    });
  };

  const disabled = !msg || !simulation || !!simulationError || rest.disabled;

  // Separate spacing classes (always applied) from styling classes (conditionally applied)
  const { spacing: spacingClasses, styling: stylingClasses } =
    separateClasses(className);

  // Determine if tooltip should be shown
  const showTooltip = simulationError && simulationError.message !== "";

  const allowanceMsg = useMemo(() => {
    if (!msg) return null;
    if (!simulationError) return null;
    if (!(simulationError instanceof InsufficientAllowanceError)) return null;
    return new MsgErc20IncreaseAllowance(msg.account, simulationError);
  }, [msg, simulationError]);

  if (allowanceMsg)
    return (
      <>
        <TxButton
          {...props}
          toastOpts={{
            loading: "Increasing Allowance",
            success: "Allowance Increased",
          }}
          label="Step (1/2): Approve"
          msg={allowanceMsg}
          onSuccess={() => doSimulate(msg)}
        />
        <InsufficientAllowanceWarning />
      </>
    );

  const modProps = {
    ...rest,
    ...(rest.label && {
      label: `${
        isSuccess
          ? "Success"
          : isSigning
            ? "Waiting"
            : isRejected
              ? "Rejected"
              : rest.label
      }`,
    }),
  };
  return (
    <>
      <Button
        {...modProps}
        onClick={handleSign}
        className={clsx(
          stylingClasses.length > 0 ? stylingClasses : "button",
          spacingClasses,
          {
            "button--grey": disabled,
            "button--waiting":
              (isSigning || isRejected || isSuccess) &&
              !className?.includes("transparent"),
            "button--success": isSuccess,
            "button--rejected": isRejected,
          }
        )}
        style={{
          cursor: isSigning || isSuccess || isRejected ? "default" : undefined,
          ...rest.style,
        }}
        disabled={disabled}
        data-tooltip-id="tx-button-tip"
        data-tooltip-html={
          showTooltip
            ? `<p class="fs-12 lh-16 fw-400 w-36 mb-0.5">${translateError(simulationError.message)}</p>`
            : undefined
        }>
        {(isSuccess || isRejected || isSigning) &&
          !className?.includes("transparent") && (
            <img
              className="w-4 h-4 filter-white big"
              src={isSigning ? txSign : isSuccess ? txSuccess : txFail}
              alt="lock"
            />
          )}
        {(isSigning || isRejected || isSuccess) &&
        className?.includes("transparent") ? (
          <Loader className="w-full h-full" />
        ) : (
          ((!isSigning && !isRejected && !isSuccess) ||
            className?.includes("transparent")) &&
          children
        )}
      </Button>
      {hideSimulation ? null : msg ? (
        <SimulationComponent simulation={simulation} error={simulationError} />
      ) : (
        <span>&nbsp;</span>
      )}
    </>
  );
};

export const TxButtonTip = () => (
  <Tooltip
    id="tx-button-tip"
    className="tooltip"
    float={true}
    style={{ zIndex: 203 }}
  />
);
export interface SimulationComponentProps {
  simulation?: Simulation;
  error?: Error;
}

const DefaultSimulationComponent = ({
  simulation,
  error,
}: SimulationComponentProps): ReactElement => {
  if (error instanceof InsufficientAllowanceError) {
    return <InsufficientAllowanceWarning />;
  }

  return (
    <small className="fs-12 text-center mt-1 color-grey block">
      <>
        {i18n.t("Network fee")}{" "}
        {simulation ? (
          <Decimal
            amount={simulation.amount}
            decimals={simulation.decimals}
            symbol={simulation.symbol}
            className="color-white"
          />
        ) : (
          <span>calculating...</span>
        )}
      </>
    </small>
  );
};

const InsufficientAllowanceWarning = () => {
  return (
    <Warning className="warning mt-2 condensed flex ai-c" color="orange">
      {/* <ExclamationCircle className="color-orange" /> */}
      <img src={lock} alt="lock" />
      <i18n.div className="text-left">
        First sign a message to give permission to use your tokens. Then you can
        sign the actual transaction.
      </i18n.div>
    </Warning>
  );
};

const successHandler = (res: TxResult): Renderable =>
  res.label ? (
    <p>{res.label}</p>
  ) : (
    <p>
      {i18n.t("Transaction Succeeded")}
      <br />
      <a
        href={networkTxLink(res)}
        target="_blank"
        className="color-white no-underline fs-12">
        {res.txHash.slice(0, 8) + "..." + res.txHash.slice(-8)}
      </a>
    </p>
  );
